"use client";

// Login / signup — the rep self-serves (PLG). Public route (outside the (rep)
// group, so no auth guard / RepShell). useTheme() falls back to dark when no
// ThemeProvider is mounted, which is fine for this surface.

import Link from "next/link";
import { useActionState, useState } from "react";
import { useTheme } from "@/lib/platform/theme/ThemeContext";
import { authenticate, type AuthState } from "./actions";

export default function LoginPage() {
  const { tokens, fonts } = useTheme();
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [state, formAction, pending] = useActionState<AuthState, FormData>(
    authenticate,
    undefined,
  );

  const inputStyle = {
    width: "100%",
    background: tokens.bg,
    border: `1px solid ${tokens.rule2}`,
    borderRadius: 8,
    color: tokens.ink,
    fontFamily: fonts.sans,
    fontSize: 15,
    padding: "11px 12px",
    marginTop: 6,
  } as const;
  const labelStyle = {
    fontFamily: fonts.mono,
    fontSize: 11,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: tokens.dim,
  } as const;

  return (
    <div
      style={{
        minHeight: "100dvh",
        background: tokens.pageBg,
        color: tokens.ink,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 360,
          background: tokens.bg2,
          border: `1px solid ${tokens.rule}`,
          borderRadius: 16,
          padding: "26px 22px",
        }}
      >
        <div
          style={{
            fontFamily: fonts.mono,
            fontSize: 13,
            letterSpacing: "0.14em",
            color: tokens.ink,
            borderBottom: `1px solid ${tokens.clay}`,
            display: "inline-block",
            paddingBottom: 2,
            marginBottom: 20,
          }}
        >
          FRONTLINE
        </div>

        <form action={formAction}>
          <input type="hidden" name="mode" value={mode} />

          {mode === "signup" && (
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="first_name" style={labelStyle}>
                First name
              </label>
              <input id="first_name" name="first_name" style={inputStyle} />
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="email" style={labelStyle}>
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 18 }}>
            <label htmlFor="password" style={labelStyle}>
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              minLength={mode === "signup" ? 8 : undefined}
              style={inputStyle}
            />
          </div>

          {mode === "login" && (
            <Link
              href="/forgot-password"
              style={{
                display: "block",
                textAlign: "right",
                marginTop: -10,
                marginBottom: 16,
                color: tokens.dim,
                fontFamily: fonts.mono,
                fontSize: 11,
                letterSpacing: "0.04em",
                textDecoration: "none",
              }}
            >
              Forgot password?
            </Link>
          )}

          {state && (
            <div
              role={"error" in state ? "alert" : "status"}
              style={{
                fontFamily: fonts.sans,
                fontSize: 13,
                color: "error" in state ? tokens.clay : tokens.dim,
                marginBottom: 14,
              }}
            >
              {"error" in state ? state.error : state.notice}
            </div>
          )}

          <button
            type="submit"
            disabled={pending}
            style={{
              width: "100%",
              background: tokens.clay,
              color: tokens.onClay,
              border: "none",
              borderRadius: 999,
              fontFamily: fonts.sans,
              fontSize: 15,
              fontWeight: 600,
              padding: "12px 0",
              cursor: pending ? "default" : "pointer",
              opacity: pending ? 0.7 : 1,
            }}
          >
            {pending
              ? "…"
              : mode === "login"
                ? "Log in"
                : "Create account"}
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMode(mode === "login" ? "signup" : "login")}
          style={{
            width: "100%",
            background: "transparent",
            border: "none",
            color: tokens.dim,
            fontFamily: fonts.mono,
            fontSize: 11,
            letterSpacing: "0.04em",
            marginTop: 16,
            cursor: "pointer",
          }}
        >
          {mode === "login"
            ? "New here? Create an account"
            : "Have an account? Log in"}
        </button>
      </div>
    </div>
  );
}
